import { useEffect, useRef, useState } from "react";

import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../../hooks/useResponsive";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { isAdmin } from "../../../../../utils/constants";
import { useCreateCardMutation } from "./useCreateCardMutation";

export function useCreateCard({
  stage,
  isTodo = false,
  newCreatedCardId,
  isCreateCardFieldOpen,
  handleCreateCardClick,
  parentCardId = null,
  boardName,
}) {
  const { id } = useBoardIdByKey();
  const [loading, setLoading] = useState(false);
  const [createBtnOpen, setCreateBtnOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [milestone] = useCustomSearchParams("milestone");
  const isMobile = useResponsive("down", "md");
  const boardPermission = boardName?.board_permission;

  const titleRef = useRef("");
  const createBtnOpenRef = useRef(false);
  const isTabVisibleRef = useRef(true);

  const { mutate: createMutate } = useCreateCardMutation({
    boardId: id,
    boardName,
    isTodo,
    parentCardId,
    milestone,
    onSuccess: (newCardId) => {
      setLoading(false);
      setTitle("");
      setCreateBtnOpen(false);
      createBtnOpenRef.current = false;
    },
    onError: (error) => {
      console.error("Error creating card", error);
      setLoading(false);
    },
  });

  const handleEnterKeyPress = () => {
    if (loading || !title?.trim()) {
      return;
    }

    handleCreateCardClick?.(null, stage.toString());

    const data = {
      title,
      board_id: id,
      ...(milestone && { epic_id: milestone }),
      ...(isTodo
        ? { is_todo: true, todo_column: "Open" }
        : { workflow_step_id: stage }),
      ...(parentCardId ? { parent_card_id: parentCardId } : {}),
    };

    setLoading(true);
    createMutate(data);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleEnterKeyPress();
    }

    if (event.key === "btnClicked") {
      handleEnterKeyPress();
    }
  };

  const openCreateButton = () => {
    setCreateBtnOpen(true);
    createBtnOpenRef.current = true;
  };

  const closeCreateButton = (shouldClose) => {
    if (shouldClose !== false && isTabVisibleRef.current) {
      setCreateBtnOpen(false);
      createBtnOpenRef.current = false;
    }
  };

  useEffect(() => {
    setCreateBtnOpen(isCreateCardFieldOpen);
    createBtnOpenRef.current = isCreateCardFieldOpen;
  }, [isCreateCardFieldOpen]);

  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      isTabVisibleRef.current = isVisible;

      if (isVisible && titleRef.current) {
        setCreateBtnOpen(true);
        createBtnOpenRef.current = true;
        setTitle(titleRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const canShowCreateButton = Boolean(
    id &&
      !isMobile &&
      (boardPermission?.can_create_task ||
        boardPermission?.is_admin ||
        isAdmin() ||
        boardName?.is_owner)
  );

  return {
    createBtnOpen,
    canShowCreateButton,
    closeCreateButton,
    handleKeyDown,
    isMobile,
    loading,
    openCreateButton,
    setTitle,
    title,
  };
}
